import { useEffect, useState } from 'react';
import { useMutation, useQuery, type QueryKey } from '@tanstack/react-query';
import { Cloud, Server, Shield } from 'lucide-react';
import {
  SUBNET_MASKS,
  oltFamily,
  type CliActionResult,
  type Olt,
  type OltVendor,
  type OltVlan,
  type Page,
} from '@simpleacs/shared';
import { api } from '../../lib/api';
import { useTasks } from '../../lib/tasks';
import { WizardDialog } from '../../components/WizardDialog';
import { Button, ErrorNotice, Field, Input, Pill, SearchSelect } from '../../components/ui';

interface Tr069Profile {
  id: string;
  name: string;
  kind: string;
  acsUrl?: string | null;
}


type Mode = 'tr069' | 'omci';

/**
 * How this ONU is managed, asked once.
 *
 * The steps follow the decision rather than the data model: what manages it,
 * then — only if that answer was TR-069 — where it informs, then the questions
 * both modes share. Which VLAN management runs on, how the ONU is addressed and
 * whether it answers from outside are the same questions either way, and an
 * operator who has just moved an ONU to OMCI still has to answer them.
 *
 * Every step is a chassis write, so the whole thing ends in a dry run the
 * operator reads before anything is sent.
 */
export function ManagementModeDialog({
  oltId,
  onuId,
  serial,
  currentMode,
  vendor,
  model,
  onClose,
  href,
  invalidate,
}: {
  oltId: string;
  onuId: string;
  serial: string;
  currentMode: Mode;
  vendor: string | null;
  /** The chassis model, for the generation — what decides which choices exist here. */
  model?: string | null;
  onClose: () => void;
  /** Where the background toast/tray row links — the ONU's own page. */
  href?: string;
  /** Extra query keys to refresh once the write lands (the ONU page's own). */
  invalidate?: QueryKey[];
}) {
  const tasks = useTasks();
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState<Mode>(currentMode);
  const [profileId, setProfileId] = useState('');
  const [priority] = useState('0');
  /** The management VLAN chosen in this dialog; '' until the current one loads. */
  const [vlan, setVlan] = useState('');
  const [vlanTouched, setVlanTouched] = useState(false);
  const [addressing, setAddressing] = useState<'dhcp' | 'static'>('dhcp');
  const [staticIp, setStaticIp] = useState('');
  const [staticMask, setStaticMask] = useState('255.255.255.0');
  const [staticGateway, setStaticGateway] = useState('');
  const [firewalled, setFirewalled] = useState(true);
  const [plan, setPlan] = useState<CliActionResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * The profiles this OLT is associated with, and which of them is its default.
   *
   * Not the org's whole list: an OLT is deliberately tied to a set, and offering
   * the rest invites pointing one subscriber at an ACS the rest of their
   * chassis does not use. The default is filled in below so the operator can
   * pass this step without opening anything — changing it is the exception, not
   * the ordinary case.
   */
  const profiles = useQuery({
    queryKey: ['olt', oltId, 'tr069-profiles'],
    queryFn: () =>
      api.get<{ items: Array<{ profileId: string; name: string; isDefault: boolean }> }>(
        `/olts/${encodeURIComponent(oltId)}/tr069-profiles`,
      ),
    enabled: mode === 'tr069',
  });

  // Preselect the OLT's default, once, and only while the operator has not
  // chosen for themselves — re-deciding under someone who has would be rude.
  useEffect(() => {
    if (profileId) return;
    const items = profiles.data?.items ?? [];
    const preferred = items.find((p) => p.isDefault) ?? items[0];
    if (preferred) setProfileId(preferred.profileId);
  }, [profiles.data, profileId]);
  // The steps that exist depend on the mode: OMCI skips the ACS entirely, which
  // is the only difference between the two paths.
  /**
   * The VLAN the ONU is managed on — asked HERE now (operator, 2026-09-09:
   * one Mngt button), the separate VLAN-only dialog is gone. Only VLANs the
   * OLT marks as management are offered, so this cannot quietly move an ONU
   * onto a subscriber VLAN; the dialog opens on the one the ONU is on. TR-069
   * needs one to mean anything: without an address the ONU never informs, so
   * the review step says so rather than sending an enable that cannot work.
   */
  const attached = useQuery({
    queryKey: ['onu-config', `/onus/${onuId}`, 'vlans'],
    queryFn: () =>
      api.get<{ items: Array<{ vlanId: number; purpose: string }> }>(
        `/onus/${encodeURIComponent(onuId)}/vlans`,
      ),
  });
  const currentVlan =
    attached.data?.items.find((v) => v.purpose === 'management')?.vlanId ?? null;
  const oltVlans = useQuery({
    queryKey: ['olt', oltId, 'vlans'],
    queryFn: () => api.get<Page<OltVlan>>(`/olts/${encodeURIComponent(oltId)}/vlans`),
  });
  const vlanOptions = (oltVlans.data?.items ?? []).filter((v) => v.usedForMgmt);
  // Open on the truth: the VLAN the ONU is on, else the first the chassis
  // marks for management — never an empty box the operator has to interpret.
  useEffect(() => {
    if (vlanTouched || vlan) return;
    if (currentVlan !== null) setVlan(String(currentVlan));
    else if (vlanOptions[0]) setVlan(String(vlanOptions[0].vlanId));
  }, [vlanTouched, vlan, currentVlan, vlanOptions]);
  const managementVlan = vlan ? Number(vlan) : null;

  /**
   * What this chassis GENERATION refuses, in the server's own words.
   *
   * The OLT DTO carries `refusals`, resolved server-side from the one
   * family-gate table — the same row the management-mode write throws from —
   * so a choice that cannot be written is disabled here with the sentence the
   * server would have refused with, rather than after a dry run. Today that
   * is one row: a STATIC management address on a ZTE C650, whose captured
   * path is `wan-ip … mode dhcp` with no static form. The model decides the
   * generation at once (so the choice is never briefly offered while the OLT
   * loads); the sentence arrives with the DTO. A cheap DB read, and cached
   * under the key the OLT page already uses.
   */
  const olt = useQuery({
    queryKey: ['olt', oltId],
    queryFn: () => api.get<Olt>(`/olts/${encodeURIComponent(oltId)}`),
  });
  const c6xx = oltFamily(vendor as OltVendor, model ?? null) === 'c6xx';
  const staticRefusal = olt.data?.refusals?.onu_static_mgmt_address ?? null;
  const staticRefused = staticRefusal !== null || (c6xx && olt.data === undefined);
  // A choice that is refused cannot stay chosen: if the dialog was opened on
  // "static" before the refusal was known, fall back to the addressing the
  // chassis takes.
  useEffect(() => {
    if (staticRefused && addressing === 'static') setAddressing('dhcp');
  }, [staticRefused, addressing]);

  const steps: Array<{ key: string; title: string }> = [
    { key: 'mode', title: 'How it is managed' },
    ...(mode === 'tr069' ? [{ key: 'profile', title: 'Which ACS' }] : []),
    { key: 'vlan', title: 'Management VLAN' },
    { key: 'addressing', title: 'Addressing' },
    { key: 'reach', title: 'Reachability' },
    { key: 'review', title: 'Review' },
  ];
  const current = steps[Math.min(step, steps.length - 1)]!;

  const body = (dryRun: boolean) => ({
    mode,
    tr069ProfileId: mode === 'tr069' && profileId ? profileId : undefined,
    managementVlan: managementVlan ?? undefined,
    vlanPriority: managementVlan === null ? undefined : Number(priority),
    addressing,
    staticIp: addressing === 'static' ? staticIp : undefined,
    staticMask: addressing === 'static' ? staticMask : undefined,
    staticGateway: addressing === 'static' && staticGateway ? staticGateway : undefined,
    firewalled,
    dryRun,
  });

  const path = `/olts/${encodeURIComponent(oltId)}/onus/${encodeURIComponent(onuId)}/management-mode`;
  const preview = useMutation({
    mutationFn: () => api.post<CliActionResult>(path, body(true)),
    onSuccess: (r) => {
      setPlan(r);
      setError(null);
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Could not build the plan'),
  });
  /**
   * Apply hands the chassis write to the background tray and closes — like
   * Resync. The mode is captured for the toast because the dialog is gone by
   * the time the write settles. The dry-run above stays in the dialog: the
   * operator reads the plan before this ever fires.
   */
  const startApply = () => {
    const chosen = mode;
    const payload = body(false);
    onClose();
    tasks.start<CliActionResult>({
      label: `Management mode — ${serial}`,
      ...(href ? { href } : {}),
      run: () => api.post<CliActionResult>(path, payload),
      done: (r) => {
        const head =
          chosen === 'tr069' ? `${serial} is on TR-069` : `${serial} is managed over OMCI from the OLT`;
        return r.warnings?.length ? `${head}, with warnings — open ${serial} to read them.` : `${head}.`;
      },
      invalidate: [['onu', onuId], ...(invalidate ?? [])],
    });
  };

  const canAdvance =
    current.key !== 'profile' || Boolean(profileId) || profiles.data?.items.length === 0;
  // A static address with no mask is not a configuration either chassis takes.
  const canLeaveAddressing =
    current.key !== 'addressing' ||
    addressing === 'dhcp' ||
    (!staticRefused && staticIp.trim().length >= 7 && staticMask.trim().length >= 7);

  const next = () => {
    setPlan(null);
    if (step + 1 === steps.length - 1) preview.mutate();
    setStep((s) => Math.min(s + 1, steps.length - 1));
  };

  return (
    <WizardDialog
      title={`Management mode · ${serial}`}
      maxWidth="xl"
      steps={steps}
      currentStep={step}
      onClose={onClose}
      footer={
        <div className="flex items-center justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <div className="flex gap-2">
            {step > 0 && (
              <Button
                onClick={() => {
                  setPlan(null);
                  setStep((s) => s - 1);
                }}
              >
                Back
              </Button>
            )}
            {current.key !== 'review' ? (
              <Button variant="primary" disabled={!canAdvance || !canLeaveAddressing} onClick={next}>
                Next
              </Button>
            ) : (
              <Button
                variant="primary"
                disabled={!plan}
                onClick={startApply}
                title={plan ? undefined : 'Waiting for the plan'}
              >
                Apply
              </Button>
            )}
          </div>
        </div>
      }
    >
      {current.key === 'mode' && (
            <div className="grid gap-3 sm:grid-cols-2">
              <ModeChoice
                icon={<Cloud className="size-4" />}
                title="TR-069"
                detail="The ONU informs an ACS, which then manages its WAN, wifi and LAN. Adds the connected-host list and remote tasks."
                selected={mode === 'tr069'}
                onSelect={() => {
                  setMode('tr069');
                  setPlan(null);
                }}
              />
              <ModeChoice
                icon={<Server className="size-4" />}
                title="OMCI only"
                detail="The OLT manages the ONU directly. Everything is set and read through the chassis; no ACS is involved."
                selected={mode === 'omci'}
                onSelect={() => {
                  setMode('omci');
                  setProfileId('');
                  setPlan(null);
                }}
              />
            </div>
          )}

          {/* Not a dismissable toast: this limitation holds for as long as the
              choice does, so the note stands wherever the choice is made
              (operator's call). A Huawei OLT has no ONT wifi command at all. */}
          {current.key === 'mode' && mode === 'omci' && vendor === 'huawei' && (
            <p className="rounded-lg border border-warn/40 bg-warn-soft px-4 py-3 text-sm text-warn">
              On a Huawei OLT, OMCI management cannot configure WiFi — the chassis has no ONT
              wifi command. SSIDs and passphrases on this ONU stay whatever the device holds
              until it is put on TR-069.
            </p>
          )}

          {current.key === 'profile' && (
            <Field
              label="TR-069 profile"
              hint="Where this ONU informs. Profiles are managed under Settings."
            >
              {profiles.isLoading ? (
                <p className="text-sm text-muted">Loading…</p>
              ) : (profiles.data?.items.length ?? 0) === 0 ? (
                <p className="text-sm text-warn">
                  This organization has no TR-069 profile yet — create one under Settings before
                  putting an ONU on TR-069.
                </p>
              ) : (
                <select
                  className="input"
                  value={profileId}
                  onChange={(e) => {
                    setProfileId(e.target.value);
                    setPlan(null);
                  }}
                >
                  {profiles.data!.items.map((p) => (
                    <option key={p.profileId} value={p.profileId}>
                      {p.name}
                      {p.isDefault ? ' — this OLT’s default' : ''}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          )}

          {current.key === 'vlan' && (
            <Field
              label="Management VLAN"
              hint="The VLAN SimpleACS and the OLT reach this ONU on. Only VLANs this OLT marks as management are offered — a subscriber VLAN here would hand the customer the route they are managed by."
            >
              {oltVlans.isLoading ? (
                <p className="text-sm text-muted">Loading the OLT’s VLANs…</p>
              ) : vlanOptions.length === 0 ? (
                <p className="text-sm text-warn">
                  This OLT has no VLAN marked for management yet — mark one on the OLT’s VLANs tab
                  first.
                </p>
              ) : (
                <select
                  className="input"
                  value={vlan}
                  onChange={(e) => {
                    setVlanTouched(true);
                    setVlan(e.target.value);
                    setPlan(null);
                  }}
                >
                  {vlanOptions.map((v) => (
                    <option key={v.vlanId} value={String(v.vlanId)}>
                      VLAN {v.vlanId}
                      {v.name ? ` — ${v.name}` : ''}
                      {currentVlan === v.vlanId ? ' (current)' : ''}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          )}

          {current.key === 'addressing' && (
            <div className="space-y-3">
              <Field label="How the ONU gets its address">
                <div className="flex gap-2">
                  {/* A refused choice is not offered (operator, 2026-10-21: hide what the
                      unit cannot take); the sentence below the tiles says why. */}
                  {(['dhcp', 'static'] as const)
                    .filter((a) => a !== 'static' || !staticRefused)
                    .map((a) => {
                    const refused = a === 'static' && staticRefused;
                    return (
                      <button
                        key={a}
                        type="button"
                        disabled={refused}
                        title={refused ? (staticRefusal ?? 'Not available on this chassis generation') : undefined}
                        onClick={() => {
                          setAddressing(a);
                          setPlan(null);
                        }}
                        className={`rounded-lg border px-3 py-1.5 text-sm ${
                          addressing === a
                            ? 'border-primary bg-primary-soft text-primary'
                            : refused
                              ? 'cursor-not-allowed border-line text-faint'
                              : 'border-line text-muted hover:text-ink'
                        }`}
                      >
                        {a === 'dhcp' ? 'DHCP' : 'Static'}
                      </button>
                    );
                  })}
                </div>
              </Field>
              {/* Not a toast: the limitation holds for as long as the chassis
                  does, so the sentence stands where the choice is made. */}
              {staticRefused && (
                <p className="rounded-lg border border-warn/40 bg-warn-soft px-4 py-3 text-sm text-warn">
                  {staticRefusal ??
                    'A static management address is not available on this chassis generation — reading why from the OLT…'}
                </p>
              )}
              {/* Both chassis want a mask straight after the address — the C300
                  as `ip-host N ip A.B.C.D mask A.B.C.D`, the MA5800 as
                  `ont ipconfig … static ip-address … mask …` — so it is asked
                  for here rather than refused on the wire. */}
              {addressing === 'static' && (
                <>
                  <Field label="Management IP" hint="The address the ONU answers on">
                    <Input
                      value={staticIp}
                      onChange={(e) => setStaticIp(e.target.value)}
                      placeholder="10.204.1.5"
                      className="font-mono"
                    />
                  </Field>
                  <Field label="Subnet mask">
                    <SearchSelect
                      value={staticMask}
                      options={SUBNET_MASKS.map((m) => ({ value: m.mask, label: m.label }))}
                      onChange={setStaticMask}
                    />
                  </Field>
                  <Field
                    label="Gateway"
                    hint="Optional on both chassis. Without one the ONU is only reachable from its own subnet, which this platform usually is not on."
                  >
                    <Input
                      value={staticGateway}
                      onChange={(e) => setStaticGateway(e.target.value)}
                      placeholder="10.204.1.1"
                      className="font-mono"
                    />
                  </Field>
                </>
              )}
            </div>
          )}

          {current.key === 'reach' && (
            <Field
              label="Reachable from outside"
              hint="Whether the ONU answers unsolicited traffic on its WAN side."
            >
              <div className="space-y-2">
                {[
                  {
                    on: true,
                    title: 'Firewalled',
                    detail: 'The ONU refuses unsolicited traffic. What a subscriber router should be.',
                  },
                  {
                    on: false,
                    title: 'Reachable',
                    detail:
                      'The ONU answers from the WAN side. Only for a device you intend to reach directly.',
                  },
                ].map((choice) => (
                  <button
                    key={String(choice.on)}
                    type="button"
                    onClick={() => {
                      setFirewalled(choice.on);
                      setPlan(null);
                    }}
                    className={`flex w-full items-start gap-2 rounded-lg border p-3 text-left ${
                      firewalled === choice.on
                        ? 'border-primary bg-primary-soft'
                        : 'border-line hover:bg-surface-2'
                    }`}
                  >
                    <Shield className="mt-0.5 size-4 shrink-0 text-muted" />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-ink">{choice.title}</span>
                      <span className="block text-xs text-muted">{choice.detail}</span>
                    </span>
                  </button>
                ))}
              </div>
            </Field>
          )}

          {current.key === 'review' && (
            <div className="space-y-3">
              {/* TR-069 without an address is the failure that looks like a
                  success: the chassis accepts every command and the ONU never
                  informs, because it has nowhere to inform from. */}
              {mode === 'tr069' && managementVlan === null && (
                <div className="rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-sm text-warn">
                  This ONU has no management VLAN, so it has no address and will never reach the
                  ACS. Go back to the Management VLAN step and pick one.
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <Pill tone="info">{mode === 'tr069' ? 'TR-069' : 'OMCI only'}</Pill>
                <Pill tone="neutral">
                  {managementVlan === null
                    ? 'no management VLAN'
                    : `VLAN ${managementVlan}`}
                </Pill>
                <Pill tone="neutral">
                  {addressing === 'dhcp' ? 'DHCP' : `static ${staticIp}/${staticMask}`}
                </Pill>
                <Pill state tone={firewalled ? 'success' : 'warn'}>
                  {firewalled ? 'firewalled' : 'reachable'}
                </Pill>
              </div>
              {preview.isPending ? (
                <p className="text-sm text-muted">Checking it against the OLT…</p>
              ) : plan ? (
                <>
                  {plan.warnings.map((w) => (
                    <div
                      key={w}
                      className="rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-sm text-warn"
                    >
                      {w}
                    </div>
                  ))}
                </>
              ) : null}
            </div>
          )}

      {error && <ErrorNotice error={new Error(error)} />}
    </WizardDialog>
  );
}

function ModeChoice({
  icon,
  title,
  detail,
  selected,
  onSelect,
}: {
  icon: React.ReactNode;
  title: string;
  detail: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`rounded-lg border p-3 text-left ${
        selected ? 'border-primary bg-primary-soft' : 'border-line hover:bg-surface-2'
      }`}
    >
      <span className="flex items-center gap-2 text-sm font-medium text-ink">
        {icon}
        {title}
      </span>
      <span className="mt-1 block text-xs text-muted">{detail}</span>
    </button>
  );
}
